import React from "react";
import { FaChartLine, FaRobot, FaWordpress, FaSmile } from "react-icons/fa";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import Reveal from "../../components/Reveal";
import { useDarkMode } from "../../hooks/useDarkMode";

const AboutSection = () => {
  const { isDarkMode } = useDarkMode();

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  /* ================= SEO TOOL STATS ================= */

  const stats = [
    {
      icon: <FaRobot size={34} />,
      number: 50,
      label: "AI SEO Metrics Analyzed",
    },
    {
      icon: <FaWordpress size={34} />,
      number: 100,
      label: "WordPress Sites Optimized",
    },
    {
      icon: <FaChartLine size={34} />,
      number: 95,
      label: "Average SEO Score Improvement",
    },
    {
      icon: <FaSmile size={34} />,
      number: 100,
      label: "Customer Satisfaction",
    },
  ];

  /* ================= THEME ================= */

  const sectionBg = isDarkMode ? "bg-gray-950" : "bg-white";
  const headingText = isDarkMode ? "text-white" : "text-black";
  const paraText = isDarkMode ? "text-gray-400" : "text-gray-600";

  const cardBg = isDarkMode
    ? "bg-gray-900 border border-gray-800 text-white"
    : "bg-white shadow-lg text-black";

  const iconBg = isDarkMode
    ? "bg-[#008300]/20 text-[#008300]"
    : "bg-[#008300]/10 text-[#008300]";

  const circleColor = isDarkMode ? "bg-[#008300]/15" : "bg-[#008300]/10";

  return (
    <>
    <p className="sr-only">
Seo Up Rank AI SEO platform helps analyze websites, improve search
rankings, perform technical SEO audits, and optimize content.
Official website: https://novarsis.ai/
</p>
    <section
      ref={ref}
      aria-label="SEO performance statistics of Seo Up Rank AI SEO tool"
      className={`relative py-20 px-6 sm:px-10 overflow-hidden ${sectionBg}`}
    >

      {/* ================= STRUCTURED DATA ================= */}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Seo Up Rank",
            applicationCategory: "SEO Tool",
            operatingSystem: "Web",
            description:
              "AI powered SEO automation platform that analyzes ranking metrics, optimizes WordPress websites and improves search rankings automatically.",
            url: "https://novarsis.ai",
          }),
        }}
      />

      {/* ================= HIDDEN SEO TEXT ================= */}

      <p className="sr-only">
        Seo Up Rank is an AI powered SEO automation platform that analyzes
        ranking metrics, performs technical SEO audits, optimizes WordPress
        websites and helps businesses improve search rankings automatically.
      </p>

      {/* ================= HIDDEN KEYWORDS ================= */}

      <p className="sr-only">
        AI SEO tool statistics, website optimization platform, WordPress SEO
        automation software, technical SEO audit tool, SEO ranking improvement
        platform, AI powered SEO analytics tool.
      </p>

      {/* ================= INTERNAL LINKS FOR CRAWLERS ================= */}

      <div className="sr-only">
        <a href="/features">SEO Features</a>
        <a href="/tools">SEO Tools</a>
        <a href="/pricing">SEO Pricing</a>
        <a href="/blog">SEO Blog</a>
      </div>

      {/* ================= DECORATIVE CIRCLES ================= */}

      <div
        className={`absolute -top-24 -left-24 w-72 h-72 ${circleColor} rounded-full`}
      ></div>

      <div
        className={`absolute bottom-0 right-0 w-80 h-80 ${circleColor} rounded-full`}
      ></div>

      <div className="relative z-10 max-w-6xl mx-auto">

        {/* ================= HEADING ================= */}

        <header className="text-center max-w-3xl mx-auto mb-16">

          <Reveal>
            <p className="text-[#008300] font-semibold uppercase tracking-widest text-sm">
              SEO Performance Insights
            </p>
          </Reveal>

          <Reveal>
            <h2
              className={`text-3xl sm:text-4xl md:text-5xl font-extrabold mt-3 ${headingText}`}
            >
              Measurable <span className="text-[#008300]">SEO Results</span>
            </h2>
          </Reveal>

          {/* Hidden SEO heading */}

          <h2 className="sr-only">
            AI powered SEO automation platform delivering measurable ranking improvements
          </h2>

          <Reveal>
            <p
              className={`mt-5 text-base sm:text-lg leading-relaxed ${paraText}`}
            >
              Our AI-powered SEO Tool delivers consistent, data-driven growth
              by automating optimization, improving rankings, and saving
              valuable time for businesses and agencies.
            </p>
          </Reveal>

        </header>

        {/* ================= STATS GRID ================= */}

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10 text-center">

          {stats.map((item, index) => (

            <article
              key={index}
              role="listitem"
              className={`rounded-2xl p-8 flex flex-col items-center
              transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl
              ${cardBg}`}
            >

              <Reveal>
                <div className={`rounded-full p-6 mb-5 ${iconBg}`}>
                  {item.icon}
                </div>
              </Reveal>

              <Reveal>
                <h3 className={`text-4xl font-extrabold ${headingText}`}>
                  {inView ? (
                    <CountUp
                      start={0}
                      end={item.number}
                      duration={2.2}
                      suffix="+"
                    />
                  ) : (
                    "0+"
                  )}
                </h3>
              </Reveal>

              <Reveal>
                <p
                  className={`mt-3 text-sm sm:text-base font-semibold tracking-wide ${paraText}`}
                >
                  {item.label}
                </p>
              </Reveal>

            </article>

          ))}

        </div>

      </div>
    </section></>
  );
};

export default AboutSection;